import React, { lazy, Suspense, useEffect, useState } from 'react';
import { Outlet, createBrowserRouter, useLocation, type RouteObject } from "react-router";
import { RouterProvider } from "react-router/dom";
import AiroErrorBoundary from '../dev-tools/src/AiroErrorBoundary';
import InboxChatWidget from './components/InboxChatWidget';
import ExitIntentPopup from './components/ExitIntentPopup';
import CookieBannerErrorBoundary from '@/components/CookieBannerErrorBoundary';
import RootLayout from './layouts/RootLayout';
import Spinner from './components/Spinner';
import { routes } from './routes';
const CookieBanner = lazy(() => import('@/components/CookieBanner').catch(error => {
  console.warn('Failed to load CookieBanner:', error);
  return {
    default: (): React.ReactElement => <></>
  };
}));
const SpinnerFallback = () => <div className="flex justify-center py-8 h-screen items-center">
    <Spinner />
  </div>;

/**
 * Locale-aware exit-intent popup.
 * Must be rendered inside the router tree so useLocation is available.
 */
function ExitIntentPopupWithLocale() {
  const {
    pathname
  } = useLocation();
  const language: 'en' | 'es' = pathname.startsWith('/es') ? 'es' : 'en';
  return <ExitIntentPopup language={language} />;
}

/**
 * Root layout component — wraps every page with shared chrome and the popup.
 * Defined as a component (not a JSX constant) so hooks inside children
 * (like useLocation) are evaluated at render time inside the router context.
 */
function RootShell() {
  return <Suspense fallback={<SpinnerFallback />}>
      <RootLayout>
        <Outlet />
      </RootLayout>
      <ExitIntentPopupWithLocale />
    </Suspense>;
}

// Wrap the agent-editable flat `routes` array in a layout route so ScrollRestoration
// + shared chrome live once above every page. Keeping the wrap here (instead of
// in routes.tsx) preserves the agent's simple flat-route contract. The dev
// boundary must live inside the route element so React Router doesn't replace it
// with its default route error UI before our boundary can catch render errors.
//
// `captureGlobalErrors={false}`: the ROOT boundary in main.tsx owns the global
// window.onerror/unhandledrejection handlers. This inner boundary only catches
// route render errors via componentDidCatch — installing window handlers here
// too would double-forward async errors and stack a second overlay.
const routeTree: RouteObject[] = [{
  element: import.meta.env.MODE === 'development' ? <AiroErrorBoundary captureGlobalErrors={false}>
          <RootShell />
        </AiroErrorBoundary> : <RootShell />,
  children: routes
}];
const router = createBrowserRouter(routeTree);
export default function App() {
  const [showCookieBanner, setShowCookieBanner] = useState(false);

  useEffect(() => {
    // Consent is needed before optional tracking can run, but its UI should not
    // compete with the first render. Show it when a visitor first engages.
    // Analytics remains consent-gated while the banner has not been opened.
    const reveal = () => setShowCookieBanner(true);
    const events: Array<keyof WindowEventMap> = ['pointerdown', 'touchstart', 'keydown', 'scroll'];
    const activate = () => {
      events.forEach(event => window.removeEventListener(event, activate));
      reveal();
    };
    events.forEach(event => window.addEventListener(event, activate, { passive: true, once: true }));
    return () => events.forEach(event => window.removeEventListener(event, activate));
  }, []);

  return <>
      <RouterProvider router={router} />
      {showCookieBanner && <CookieBannerErrorBoundary>
          <Suspense fallback={null}>
            <CookieBanner />
          </Suspense>
        </CookieBannerErrorBoundary>}
      <InboxChatWidget />
    </>;
}
